// Operator-facing vocabulary — ATC-109 / `ATC-ADR-018` anchor 6.
//
// THE LABEL AN OPERATOR READS AND THE VALUE ON THE WIRE ARE DIFFERENT THINGS,
// and the mapping lives in EXACTLY ONE MODULE — this one — applied at the
// render boundary. A vocabulary change that reached the wire would be a
// cross-repo contract change for a labelling preference.
//
// Nothing here is ever sent. ATC's console does not submit a `migrationType` at
// all (devices are registered with MINT by Flight Deck Manager, not by an
// operator), so there is no reverse map to keep in step — but if a write
// surface ever gains one, the reversal belongs HERE and nowhere else.

/**
 * Contract `vocabulary.migrationType`. Wire value → operator noun.
 *
 * Kept separate from `migrationTypeLabel` so `tokenParity.test.ts` can assert
 * it against the vendored contract directly.
 */
export const MIGRATION_TYPE_NOUN: Readonly<Record<string, string>> = {
  D2C: 'Hybrid',
  T2T: 'Cloud',
}

/**
 * Render a `migrationType` for an operator: `Hybrid (D2C)` / `Cloud (T2T)`.
 *
 * ATC SHOWS BOTH FORMS, where the style guide's §1.9 maps only `D2C → Hybrid`.
 * That is deliberate (`ATC-ADR-018`): the operator reading this console is the
 * same person who greps device logs for `D2C`, curls Bootstrap with
 * `migrationType=T2T`, and reads `atc-ppkg.yaml`'s prose about T2T migrations.
 * A bare rename would make the control-plane console disagree with every other
 * ATC artifact that operator touches. Showing both honours the vocabulary
 * without breaking wire-value recognition.
 *
 * An UNKNOWN value passes through UNCHANGED rather than becoming a blank or an
 * em-dash. `MigrationType` is not a union in `api/types.ts` and is not narrowed
 * by this task, because MINT is free to add one: a third migration type would
 * then render as its raw wire value, which is legible, rather than vanishing.
 */
export function migrationTypeLabel(wireValue: string): string {
  const noun = MIGRATION_TYPE_NOUN[wireValue]
  return noun ? `${noun} (${wireValue})` : wireValue
}

/**
 * The console's own name, which is NOT the Flight Deck wordmark.
 *
 * §1.6's wordmark rule (`Flight` extrabold + `Deck` normal, one word, capital
 * D) governs the string `FlightDeck`, and ATC'S PRODUCT IS NOT FLIGHTDECK. The
 * text is styled and de-duplicated in `ATC-110`; it is NEVER replaced.
 *
 * Stated as a constant rather than only in prose because an agent reading §1.6
 * alongside the style guide's note that "ATC's brand is currently the literal
 * text 'ATC Operator Console'" will plausibly conclude the opposite.
 */
export const CONSOLE_NAME = 'ATC Operator Console'

// ══ Provisioning vocabulary (ATC-129 / R-ATC-10) ═══════════════════════════
//
// ANY OPERATOR-FACING LABEL MAPPING BELONGS IN THIS MODULE AND NOWHERE ELSE.
// That is the rule this file's header states for `migrationType`, and it is not
// specific to migration types: the moment a second module starts turning a wire
// value into English, the two disagree and an operator sees one word on a list
// and a different word on the detail page for the same thing.

/**
 * `ATC-ADR-016`'s principal class, for a human.
 *
 * BOTH FORMS, on the same reasoning as `migrationTypeLabel`: the operator
 * reading this console is the same person who reads `atc_fd_` in a log line and
 * `principalClass` in a request body. A bare "Flight Deck" would make the
 * console the only place in ATC that does not say `flightdeck`.
 */
export function principalClassLabel(principalClass: string): string {
  switch (principalClass) {
    case 'device':
      return 'Device (atc_lt_)'
    case 'flightdeck':
      return 'Flight Deck (atc_fd_)'
    default:
      // Unknown passes through unchanged rather than becoming blank — the same
      // rule `migrationTypeLabel` follows, for the same reason.
      return principalClass
  }
}

/**
 * Server refusal code → a sentence an operator can act on.
 *
 * WHAT THIS IS FOR. Every one of these codes is precise and none of them says
 * what to DO. `pool_capacity_zero_not_permitted` is exactly right and tells an
 * operator nothing; the sentence beside it explains that taking a pool to zero
 * would silently convert a hard-capped customer into an unlimited one, which is
 * the fact that makes the refusal make sense.
 *
 * THE FALLBACK IS THE SERVER'S OWN MESSAGE, NEVER BLANK. A code this map does
 * not know is a code from a newer server, and the server's message is better
 * than nothing by a wide margin. Rendering blank would turn an informative
 * refusal into a silent failure — which is the single worst outcome on a write
 * surface, and the thing most of this sprint's design exists to avoid.
 */
const REFUSAL_SENTENCES: Readonly<Record<string, string>> = {
  // --- Entitlement edit (ATC-074) ---
  entitlement_set_empty:
    'A credential cannot be left entitled to nothing. That is not a revocation — set the status to revoked instead, which is named, audited and reversible.',
  entitlement_contradictory:
    'The same entitlement was both added and removed, so the result would depend on which was applied first. Pick one.',
  entitlement_unsupported:
    'One of these entitlements is not a service ATC knows about. A stored typo is silently ineffective — the service simply returns 403 at runtime with nothing pointing at the cause.',
  ppkg_requires_flightdeck_class:
    'The ppkg entitlement is grantable only on the Flight Deck credential, never the device one — a build endpoint reachable with a token that lives on every device would be a package factory for a tenant-join credential.',

  // --- Credential selection (ATC-ADR-016) ---
  partition_ambiguous:
    'This environment holds more than one credential, so the request has to say which. Choose the device or the Flight Deck credential.',
  principal_not_found:
    'This environment has no credential of that class.',
  flightdeck_principal_already_issued:
    'This environment already has a Flight Deck credential. Rotate the existing one rather than issuing a second.',

  // --- Migration-licence adjustment (ATC-122) ---
  adjustment_below_committed:
    'The correction would take the balance below what is already committed to running migrations, which would retroactively invalidate work in flight.',
  pool_capacity_zero_not_permitted:
    'Taking a pool to exactly zero would silently turn a hard-capped customer into an unlimited one: checkout would stop blocking and start drawing consumable credits with no error anywhere. Deprovisioning a pool is a separate decision.',
  reason_contains_token_material:
    'The reason looks like it contains a licence token. It is stored and logged, so it must not carry credential material.',
  reason_missing: 'A reason is required. A balance reduction with no stated cause is indistinguishable from a defect later.',
  reason_too_long: 'The reason is too long — 256 characters maximum.',
  amount_invalid: 'The amount must be a negative whole number. Use a grant to add capacity.',

  // --- Expiry (ATC-121) ---
  expires_at_in_past:
    'Issuance will not mint an already-dead credential: the raw token is shown exactly once, so a dead-on-arrival credential cannot be recovered without issuing again. Set an expiry after the fact instead.',
  expiry_action_contradictory:
    'Setting an expiry and clearing it are contradictory in one request. Pick one.',
  expires_at_invalid:
    'The expiry must be a full ISO-8601 instant. A bare date is refused because midnight and end-of-day differ by a day on a kill switch.',

  // --- Environment deletion (ATC-142) ---
  environment_active:
    'This environment still has an active credential. Suspend or revoke it first — a delete must never be the first thing that stops a live fleet, and revocation is reversible where deletion is not.',
  environment_not_found:
    'No environment exists with that id. Nothing was deleted — the id may be mistyped, or it may already be gone.',
  request_body_not_supported:
    'Deleting an environment takes no options. There is no force or cascade: an active environment has to be suspended or revoked first.',

  // --- Request shape (Hotfix 038) ---
  request_body_unrecognized_field:
    'The request carried a field the server does not recognise — usually a misspelling. It is refused rather than ignored, because a silently dropped field means the change did not do what you asked.',
  patch_actions_mutually_exclusive:
    'Only one change per request: status, token rotation, entitlements, or expiry. Each produces its own audit record.',
  patch_action_missing: 'The request asked for no change.',
}

/**
 * @param code    the server's `error.code`, if any
 * @param message the server's `error.message` — the fallback, and never blank
 */
export function refusalSentence(code: string | undefined, message: string): string {
  if (code && REFUSAL_SENTENCES[code]) {
    return REFUSAL_SENTENCES[code]
  }
  return message
}

/** Exported for the test that pins the map against the codes the server emits. */
export const KNOWN_REFUSAL_CODES: readonly string[] = Object.keys(REFUSAL_SENTENCES)
